import { Form, useFormApi } from "informedv4";
import * as React from "react";
import { Link } from "react-router-dom";
import { Device, Site, Subject } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { ApiClients, ApiPutClientById } from "../../misc/api/AccountServer/AccountApi";
import { QueryCondition, QueryOrderDirection } from "../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import DocumentTitle from "../../misc/components/DocumentTitle";
import { DeviceForm, DevicesFormModel } from "./DeviceForm";
import { DevicesActionButtons } from "./DevicesActionButtons";
import { Authorization } from "../../misc/components/Authorization";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../../Core/SecurityContext";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { OrderDirection, Package, VitalographUpdateClient } from "@vitalograph/update-client";
import { useAboutContextProvider } from "../../Core/AboutContextProvider";

declare const rootUrl: string;

export const CreateDeviceForm = () => {
    const { study, deviceTypes } = useCurrentStudyConfigurationContext();

    const client = useWebPortalApiClient();
    const [error, setError] = React.useState<string>();
    const [successAlert, setSuccessAlert] = React.useState<boolean>(false);
    const [sites, setSites] = React.useState<Site[]>([]);
    const [subjects, setSubjects] = React.useState<Subject[]>([]);
    const [submitting, setSubmitting] = React.useState<boolean>(false);

    const { about } = useAboutContextProvider();
    const [updateClient, setUpdateClient] = React.useState(new VitalographUpdateClient(about.UpdateServiceUri));
    const [clientDevices, setClientDevices] = React.useState([]);
    const [packages, setPackages] = React.useState<Package[]>([]);
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const getDropDowns = async () => {
            setError(undefined);
            const sitesResult = await client.client.getSites(`id,number`, `studyId eq ${study.id}`, "number");
            setSites(sitesResult.value);

            if (sitesResult.value.length === 0)
                setError("There are no sites available to allocate in this study.");

            const subjectsResult = await client.client.getSubjects(`id,number`, `site/studyId eq ${study.id}`, "number");
            setSubjects(subjectsResult.value);

            try {
                const clients = await ApiClients({
                    Filters: [
                        { Member: "serial", Condition: QueryCondition.NotEqual, Values: [""] },
                        { Member: "serial", Condition: QueryCondition.NotEqual, Values: [null] },
                        { Member: "portal", Condition: QueryCondition.Equal, Values: ["", null, rootUrl, rootUrl.slice(0, -1)] },
                    ],
                    Order: {
                        Direction: QueryOrderDirection.Ascending,
                        Member: "serial"
                    }
                });

                const deviceClients = clients.data.filter(c => c.scopes.includes("device"));

                setClientDevices(deviceClients);

            } catch (ex) {
                setError("Unable to connect to Vitalograph Account to query for devices");
            }

            try {

                const packageName = await legacyClient.getSetting("spirotrac6.configuration.packageName",study.id);
                const packagesResult = await updateClient.package_PostPackages({
                    filters: [
                        {
                            member: "Name",
                            values: [
                                packageName
                            ],
                        }
                    ],
                    order: {
                        member: "Version",
                        direction: OrderDirection.Descending
                    }
                });
                setPackages(packagesResult.data)
            } catch (ex) {
                setError("Unable to connect to Vitalograph Update Service to query Packages");
            }
        }

        getDropDowns();
    }, [study]);

    const formApi = useFormApi();

    const mapFormToModel = (form: DevicesFormModel): Device => {
        return ({
            studyId: study.id,
            deviceType: form.deviceType.id,
            serialNumber: form.serialNumber.serial,
            allocatedSiteId: form.allocatedSite?.id,
            allocatedSubjectId: form.allocatedSubject?.id,
            spirotracPackageVersion: form.spirotracPackageVersion?.version
        });
    }

    const onSubmit = async () => {
        setSubmitting(true);
        setError(undefined);
        setSuccessAlert(false);

        formApi.validate();
        const state = formApi.getFormState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setError("Errors exist on the page");
        }

        try {
            const updatedClientModel = {
                ...(state.values as any).serialNumber,
                portal: rootUrl.replace(/\/$/, "")
            }

            const device = await client.client.postDevice(mapFormToModel(state.values as any));

            if (device) {
                setSuccessAlert(true);
            }
        } catch (ex) {
            setError(ex.message);
        } finally {
            setSubmitting(false);
        }

        setSubmitting(false);
    }

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/deviceadministration`}>Device Administration list</Link>?
            </Alert>
            <Alert show={!!error} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Create Device</h2>
                </div>
            </div>
            <DeviceForm editMode={false} clientDevices={clientDevices} sites={sites} subjects={subjects} deviceTypes={deviceTypes.map(d => ({
                id: d.Id,
                name: d.Name
            }))} spirotracPackages={packages} />
            <div className="row">
                <div className="col-md-12">
                    <DevicesActionButtons submitting={submitting} onSaveClick={onSubmit} automationIdPrefix="ActionsHeader" />
                </div>
            </div>
        </>
    );
}

export const CreateDeviceScreen = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();

    return (<DocumentTitle title="Create Device | Vitalograph Web Portal">
        <Authorization currentPermissions={currentUserPermissions} studyId={study.id} permissionId={PermissionEnum.ViewDevices}>
            <Form validateOn="submit">
                <CreateDeviceForm />
            </Form>
        </Authorization>
    </DocumentTitle>);
}
